<script setup lang="ts">
import { ArrowRight, AtSign, Pencil, RefreshCw, ShieldAlert, ShieldCheck } from '@lucide/vue'
import type { LoginIdentitySelection } from '~~/shared/schemas/auth'
import { safeRedirect } from '~~/shared/utils/auth'

type Resolution =
  | { kind: 'not-found' }
  | { kind: 'routing-conflict' }
  | { kind: 'identity-conflict' }
  | { kind: 'local'; challengeId: string; displayIdentifier: string }
  | {
      kind: 'oidc'
      challengeId: string
      displayIdentifier: string
      provider: { key: string; displayName: string; buttonLabel: string }
      behavior: 'button' | 'redirect'
    }

const route = useRoute()
const analytics = useAnalytics()
const { data: bootstrap } = await useFetch('/api/auth/bootstrap')
const auth = useAuth()
const identifier = ref('')
const password = ref('')
const identitySelection = ref<LoginIdentitySelection>()
const resolution = ref<Resolution>()
const step = ref<1 | 2>(1)
const direction = ref<'forward' | 'back'>('forward')
const pending = ref(false)
const redirecting = ref(false)
const error = ref('')
const viewportHeight = ref<number>()
const identifierInput = useTemplateRef<HTMLInputElement>('identifierInput')
const passwordInput = useTemplateRef<{ focus: () => void }>('passwordInput')
const firstPanel = useTemplateRef<HTMLElement>('firstPanel')
const secondPanel = useTemplateRef<HTMLElement>('secondPanel')

const oidcErrorMessages: Record<string, string> = {
  'request-expired': 'La demande de connexion a expiré ou a déjà été utilisée.',
  'claims-missing': 'Un attribut obligatoire n’a pas été transmis par le fournisseur.',
  'email-unverified': 'Le fournisseur n’a pas confirmé votre adresse e-mail.',
  'link-ambiguous': 'Cette identité ne peut pas être rattachée automatiquement.',
  'request-invalid': 'La réponse ne correspond pas à la demande initiale.',
  'provider-response': 'La réponse du fournisseur n’a pas pu être validée.',
  'callback-failed': 'La connexion OpenID Connect n’a pas pu être finalisée.',
}
const oidcError = computed(() => {
  const value = Array.isArray(route.query.oidc_error)
    ? route.query.oidc_error[0]
    : route.query.oidc_error
  return typeof value === 'string' ? oidcErrorMessages[value] : undefined
})
const localResolution = computed(() => resolution.value?.kind === 'local')
const oidcResolution = computed(() =>
  resolution.value?.kind === 'oidc' ? resolution.value : undefined,
)

function postLoginRedirect() {
  const redirect = safeRedirect(route.query.redirect)
  return redirect.startsWith('/api/') ? '/' : redirect
}
function oidcUrl(value = oidcResolution.value) {
  if (!value) return '#'
  return `/api/auth/oidc/${encodeURIComponent(value.provider.key)}/start?challenge=${encodeURIComponent(value.challengeId)}`
}
function persistOidc(value: Extract<Resolution, { kind: 'oidc' }>) {
  sessionStorage.setItem('morning-login-resolution', JSON.stringify(value))
}
function updateViewportHeight() {
  viewportHeight.value = (step.value === 1 ? firstPanel.value : secondPanel.value)?.scrollHeight
}
let panelObserver: ResizeObserver | undefined
onMounted(() => {
  panelObserver = new ResizeObserver(updateViewportHeight)
  if (firstPanel.value) panelObserver.observe(firstPanel.value)
  if (secondPanel.value) panelObserver.observe(secondPanel.value)
})
onBeforeUnmount(() => panelObserver?.disconnect())
async function resizeAndFocus() {
  await nextTick()
  updateViewportHeight()
  const focusDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 1 : 310
  window.setTimeout(() => {
    if (step.value === 1) identifierInput.value?.focus()
    else if (localResolution.value) passwordInput.value?.focus()
  }, focusDelay)
}
watch(
  [error, identitySelection],
  async () => {
    await nextTick()
    updateViewportHeight()
  },
  { flush: 'post' },
)
async function showSecond() {
  direction.value = 'forward'
  step.value = 2
  await resizeAndFocus()
}
async function editIdentifier() {
  direction.value = 'back'
  step.value = 1
  password.value = ''
  identitySelection.value = undefined
  error.value = ''
  redirecting.value = false
  await resizeAndFocus()
}
async function resolveIdentifier() {
  pending.value = true
  error.value = ''
  try {
    const value = await $fetch<Resolution>(
      `/api/auth/resolve?redirect=${encodeURIComponent(postLoginRedirect())}`,
      { method: 'POST', body: { identifier: identifier.value } },
    )
    resolution.value = value
    if (value.kind === 'not-found') {
      error.value = 'Compte inexistant.'
      analytics.trackEvent('operation_failure', {
        domain: 'auth',
        action: 'resolve',
        status_category: '4xx',
      })
      return
    }
    if (value.kind === 'routing-conflict') {
      error.value = 'Configuration de connexion ambiguë. Contactez un administrateur.'
      analytics.trackEvent('operation_failure', {
        domain: 'auth',
        action: 'resolve_routing_conflict',
        status_category: '4xx',
      })
      return
    }
    if (value.kind === 'identity-conflict') {
      error.value =
        'Plusieurs identités correspondent. Utilisez la connexion dédiée de votre entreprise ou votre identifiant Morning unique.'
      analytics.trackEvent('operation_failure', {
        domain: 'auth',
        action: 'resolve_identity_conflict',
        status_category: '4xx',
      })
      return
    }
    identifier.value = value.displayIdentifier
    analytics.trackEvent('auth_action', {
      action: 'resolve',
      method: value.kind === 'oidc' ? 'oidc' : 'local',
      result: 'success',
    })
    await showSecond()
    if (value.kind === 'oidc' && value.behavior === 'redirect') {
      redirecting.value = true
      persistOidc(value)
      window.setTimeout(() => window.location.assign(oidcUrl(value)), 330)
    }
  } catch (cause) {
    error.value = 'Connexion impossible. Réessayez dans quelques instants.'
    analytics.trackEvent('operation_failure', {
      domain: 'auth',
      action: 'resolve',
      status_category: analyticsStatusCategory(cause),
    })
  } finally {
    pending.value = false
  }
}
async function selectIdentity(identityId: string) {
  if (!identitySelection.value || pending.value) return
  pending.value = true
  error.value = ''
  try {
    await $fetch('/api/auth/identity-select', {
      method: 'POST',
      body: { challengeId: identitySelection.value.challengeId, identityId },
    })
    identitySelection.value = undefined
    await auth.refresh()
    await navigateTo(postLoginRedirect())
  } catch {
    identitySelection.value = undefined
    editIdentifier()
    error.value = 'La demande a expiré. Recommencez votre connexion.'
  } finally {
    pending.value = false
  }
}

async function submitPassword() {
  const current = resolution.value
  if (current?.kind !== 'local') return
  pending.value = true
  error.value = ''
  try {
    const result = await $fetch<{ authenticated: boolean; selection?: LoginIdentitySelection }>(
      '/api/auth/login',
      {
        method: 'POST',
        body: { challengeId: current.challengeId, password: password.value },
      },
    )
    password.value = ''
    if (result.selection) {
      identitySelection.value = result.selection
      return
    }
    await auth.refresh()
    analytics.trackEvent('auth_action', {
      action: 'login',
      method: 'local',
      result: 'success',
      tenant_count_bucket: analyticsCountBucket(auth.accessibleTenantCount.value),
    })
    await navigateTo(
      auth.tenantSelectionRequired.value
        ? { path: '/tenant-select', query: { redirect: postLoginRedirect() } }
        : postLoginRedirect(),
    )
  } catch (cause) {
    error.value = 'Identifiant ou mot de passe incorrect.'
    analytics.trackEvent('operation_failure', {
      domain: 'auth',
      action: 'login_invalid_credentials',
      status_category: analyticsStatusCategory(cause),
    })
    try {
      const renewed = await $fetch<Resolution>(
        `/api/auth/resolve?redirect=${encodeURIComponent(postLoginRedirect())}`,
        { method: 'POST', body: { identifier: identifier.value } },
      )
      if (renewed.kind === 'local') resolution.value = renewed
    } catch {
      // Le message public reste volontairement générique.
    }
  } finally {
    pending.value = false
  }
}
function beginOidc() {
  const value = oidcResolution.value
  if (!value) return
  persistOidc(value)
  analytics.trackEvent('auth_action', { action: 'oidc_start', method: 'oidc', result: 'attempt' })
  window.location.assign(oidcUrl(value))
}

onMounted(async () => {
  if (oidcError.value) {
    try {
      const stored = JSON.parse(
        sessionStorage.getItem('morning-login-resolution') ?? '',
      ) as Resolution
      if (stored.kind === 'oidc') {
        identifier.value = stored.displayIdentifier
        const renewed = await $fetch<Resolution>(
          `/api/auth/resolve?redirect=${encodeURIComponent(postLoginRedirect())}`,
          { method: 'POST', body: { identifier: stored.displayIdentifier } },
        )
        resolution.value = renewed.kind === 'oidc' ? renewed : stored
        step.value = 2
      }
    } catch {
      // Une erreur sans contexte revient au premier écran.
    }
  }
  await resizeAndFocus()
})
useHead({ title: 'Connexion · Morning' })
</script>

<template>
  <AuthShell
    v-if="bootstrap"
    :app="bootstrap.app"
  >
    <div
      class="auth-slider-viewport analytics-sensitive"
      :style="viewportHeight ? { height: `${viewportHeight}px` } : undefined"
    >
      <div
        class="auth-slider-track"
        :class="[`is-step-${step}`, `is-${direction}`]"
      >
        <section
          ref="firstPanel"
          class="auth-slide"
          :aria-hidden="step !== 1"
        >
          <div class="auth-heading">
            <p class="auth-eyebrow">Espace sécurisé</p>
            <h1>Ravi de vous revoir.</h1>
            <p class="auth-intro">Indiquez votre compte pour choisir la méthode de connexion.</p>
          </div>
          <form
            class="auth-form"
            @submit.prevent="resolveIdentifier"
          >
            <label class="auth-field">
              <span class="auth-label">Adresse e-mail ou nom d’utilisateur</span>
              <span class="auth-input-wrap">
                <AtSign
                  :size="18"
                  aria-hidden="true"
                />
                <input
                  ref="identifierInput"
                  v-model="identifier"
                  name="identifier"
                  autocomplete="username"
                  autocapitalize="none"
                  spellcheck="false"
                  maxlength="254"
                  required
                  placeholder="vous@exemple.fr"
                />
              </span>
            </label>
            <p
              v-if="error && step === 1"
              class="auth-error auth-login-error"
              role="alert"
            >
              <span aria-hidden="true">!</span
              ><span class="auth-login-error-copy">{{ error }}</span>
            </p>
            <UiButton
              variant="solid"
              tone="accent"
              type="submit"
              :disabled="pending"
            >
              <span>{{ pending ? 'Vérification…' : 'Continuer' }}</span>
              <span
                v-if="pending"
                class="auth-spinner"
                aria-hidden="true"
              />
              <ArrowRight
                v-else
                :size="17"
                aria-hidden="true"
              />
            </UiButton>
          </form>
        </section>

        <section
          ref="secondPanel"
          class="auth-slide"
          :aria-hidden="step !== 2"
        >
          <div class="auth-heading auth-heading-compact">
            <p class="auth-eyebrow">Authentification</p>
            <h1>Confirmez votre connexion.</h1>
          </div>
          <div class="auth-identifier-chip">
            <span
              ><AtSign
                :size="15"
                aria-hidden="true"
              />{{ identifier }}</span
            >
            <button
              type="button"
              @click="editIdentifier"
            >
              <Pencil
                :size="14"
                aria-hidden="true"
              />Modifier
            </button>
          </div>

          <UiFormSection
            v-if="identitySelection"
            class="auth-identity-selection"
            title="Choisir une identité"
          >
            <p>Mot de passe validé. Choisissez votre entreprise pour continuer.</p>
            <UiButton
              v-for="candidate in identitySelection.identities"
              :key="candidate.id"
              class="auth-identity-choice"
              variant="outline"
              :disabled="pending"
              @click="selectIdentity(candidate.id)"
            >
              {{ candidate.tenantName }} · {{ candidate.username }} · {{ candidate.email }}
            </UiButton>
          </UiFormSection>
          <form
            v-else-if="localResolution"
            class="auth-form"
            @submit.prevent="submitPassword"
          >
            <AuthPasswordField
              ref="passwordInput"
              v-model="password"
              label="Mot de passe"
              name="password"
              autocomplete="current-password"
              placeholder="Votre mot de passe"
            />
            <p
              v-if="error"
              class="auth-error auth-login-error"
              role="alert"
            >
              <span aria-hidden="true">!</span
              ><span class="auth-login-error-copy">{{ error }}</span>
            </p>
            <UiButton
              variant="solid"
              tone="accent"
              type="submit"
              :disabled="pending"
            >
              <span>{{ pending ? 'Connexion en cours…' : 'Se connecter' }}</span>
              <span
                v-if="pending"
                class="auth-spinner"
                aria-hidden="true"
              />
            </UiButton>
          </form>

          <div
            v-else-if="oidcResolution"
            class="auth-form"
          >
            <section
              v-if="oidcError"
              class="auth-oidc-feedback"
              role="alert"
            >
              <span class="auth-oidc-feedback-icon"><ShieldAlert :size="18" /></span>
              <span class="auth-oidc-feedback-copy">
                <strong>Connexion {{ oidcResolution.provider.displayName }} impossible</strong>
                <small>{{ oidcError }}</small>
              </span>
            </section>
            <div
              v-if="redirecting"
              class="auth-redirecting"
              role="status"
              aria-live="polite"
            >
              <span
                class="auth-spinner"
                aria-hidden="true"
              />
              <strong>Redirection vers {{ oidcResolution.provider.displayName }}…</strong>
            </div>
            <button
              v-else
              class="auth-oidc-button auth-oidc-button-primary"
              type="button"
              @click="beginOidc"
            >
              <span class="auth-oidc-icon"><ShieldCheck :size="19" /></span>
              <span class="auth-oidc-copy">
                <small>{{ oidcResolution.provider.displayName }}</small>
                <strong>{{ oidcError ? 'Réessayer' : oidcResolution.provider.buttonLabel }}</strong>
              </span>
              <RefreshCw
                v-if="oidcError"
                :size="17"
                aria-hidden="true"
              />
              <ArrowRight
                v-else
                :size="17"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="oidcError"
              class="auth-secondary-action"
              type="button"
              @click="editIdentifier"
            >
              Changer de compte
            </button>
          </div>
        </section>
      </div>
    </div>
  </AuthShell>
</template>
